import { useState, Fragment, useCallback } from "react";
import { useDialog } from "../../dialog";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { useGridSettingsSelect, useUpdateGridSettings } from "../../../data";
import type { TGridSettings } from "../../../data";

type ConfirmationParams = {
  title: string;
  message: string;
  confirmButtonText: string;
  variant?: "danger" | "neutral";
  dontShowAgainKey?: keyof TGridSettings;
  onConfirm: () => void;
  onCancel?: () => void;
};

export const useConfirmation = () => {
  const gridSettings = useGridSettingsSelect((s) => s);
  const { optimistic: updateGridSettings } = useUpdateGridSettings();
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const ConfirmDialogContent = useCallback(
    (p: { open: boolean; arg?: ConfirmationParams; close: () => void }): React.ReactElement => {
      if (!p.arg) return <Fragment />;

      return (
        <ConfirmDialog
          open={p.open}
          title={p.arg.title}
          message={p.arg.message}
          variant={p.arg.variant ?? "neutral"}
          confirmButtonText={p.arg.confirmButtonText}
          onCancel={() => {
            setDontShowAgain(false);
            p.close();
            p.arg?.onCancel?.();
          }}
          onConfirm={() => {
            if (dontShowAgain && p.arg?.dontShowAgainKey) {
              updateGridSettings({ [p.arg.dontShowAgainKey]: true } as Partial<TGridSettings>);
            }
            setDontShowAgain(false);
            p.close();
            p.arg?.onConfirm();
          }}
          showCheckbox={!!p.arg?.dontShowAgainKey}
          checkboxLabel="Don't ask me this again"
          checkboxChecked={dontShowAgain}
          onCheckboxChange={setDontShowAgain}
        />
      );
    },
    [updateGridSettings, dontShowAgain],
  );

  const openConfirm = useDialog<ConfirmationParams>(ConfirmDialogContent);

  return (params: ConfirmationParams) => {
    // Check if user has disabled this confirmation
    if (params.dontShowAgainKey && gridSettings.data?.[params.dontShowAgainKey]) {
      params.onConfirm();
      return;
    }

    openConfirm(params);
  };
};
